import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import H6 from '@base/H6/H6';
import H4 from '@base/H4/H4';
import Link from '@base/Link/Link';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import analyticsAction from '@actions/analytics';
import { Spacings } from '../../styleConstants';

const NavBarWrapper = styled.div`
  position: relative;
  grid-column: 1/6;
  z-index: 2;
`;

const NavBarFixed = styled.div`
  position: sticky;
  width: 225px;
  top: ${Spacings.SPACING_HEADER + Spacings.SPACING_HEADER_MARGIN}px;
`;

const Selected = styled.div`
  height: ${({ selectedElHeight }) => selectedElHeight}px;
  width: 100%;
  position: absolute;
  background-color: ${({ theme }) => theme.colors.white};
  top: ${({ selectedElPos, selectedElHeight }) =>
    selectedElPos + 46 + (selectedElHeight > 40 ? 10 : 5)}px;
  transition: all 400ms ease-in;
  transition-property: top, height;
  border-radius: 8px;
  box-shadow: ${({ theme }) =>
    boxShadowGenerator({
      yOffset: Spacings.SPACING_1B,
      blur: Spacings.SPACING_4B,
      theme
    })};
`;

const NavItems = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
`;

const MainItem = styled(H4)`
  padding: 8px 16px;
  color: ${({ theme }) => theme.colors.fontGrey};
`;

const Item = styled(H6)`
  padding: 8px 16px;
  transition: color 400ms ease-in-out;
  color: ${({ selected, theme }) =>
    selected ? theme.colors.green : theme.colors.fontLightGrey};
  font-weight: ${({ selected }) => (selected ? 600 : 'normal')};
  opacity: 1;
  cursor: pointer;
  :hover {
    color: ${({ theme, selected }) =>
      selected ? theme.colors.green : theme.colors.fontGrey};
  }
`;

const MenuContainer = styled.div`
  margin-bottom: 24px;
`;

const HelpCenterNavBar = ({ display }) => {
  const router = useRouter();
  const { categoryID, articleID } = router.query;
  const [selectedNav, setSelectedNav] = useState(categoryID);
  const categories = useSelector(
    ({ helpCenter }) => helpCenter.categories?.results || []
  );

  const [selectedElHeight, setselectedElHeight] = useState(0);
  const [selectedElPos, setselectedElPos] = useState(0);

  useEffect(() => {
    setSelectedNav(parseInt(categoryID, 10));
    const el = document.querySelector(`[data-id="${categoryID}"]`);
    const selectedElemHeight = el?.offsetHeight;
    const selectedElemOffset = el?.offsetTop;
    setselectedElHeight(selectedElemHeight);
    setselectedElPos(selectedElemOffset - (selectedElemHeight > 40 ? 10 : 6));
  }, [categoryID]);

  const dispatch = useDispatch();
  if (!display) return null;
  const onHeaderClick = () => {
    dispatch(
      analyticsAction('help-center-clicked', '', null, {
        'Faq Block Id': categoryID,
        'Faq Id': articleID,
        location: 'help center nav-bar',
        path: router.asPath
      })
    );
  };
  const onNavItemClick = (category) => () => {
    setSelectedNav(category.id);
    dispatch(
      analyticsAction('help-center-category-clicked', '', null, {
        'Faq Block Id': category.id,
        'Screen Name': category.name,
        'Faq Id': articleID
      })
    );
  };

  return (
    <NavBarWrapper>
      <NavBarFixed>
        <Link href="/helpcenter" as="/helpcenter" onClick={onHeaderClick}>
          <MainItem>Help center</MainItem>
        </Link>
        <Selected
          selectedElPos={selectedElPos}
          selectedElHeight={selectedElHeight}
        />
        <NavItems>
          <MenuContainer>
            {categories.map((category) => (
              <Link
                href="/helpcenter/category/[categoryID]/articles"
                as={`/helpcenter/category/${category.id}/articles`}
                key={category.id}
                onClick={onNavItemClick(category)}
              >
                <Item
                  selected={selectedNav === category.id}
                  data-id={category.id}
                >
                  {category.name}
                </Item>
              </Link>
            ))}
          </MenuContainer>
        </NavItems>
      </NavBarFixed>
    </NavBarWrapper>
  );
};

export default HelpCenterNavBar;

HelpCenterNavBar.propTypes = {
  display: PropTypes.bool
};

HelpCenterNavBar.defaultProps = {
  display: false
};
